import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Zap, TrendingUp, Lock, Globe, ArrowRight, ChevronDown, BarChart3 } from 'lucide-react';
import Logo from '../components/Logo';

import { fmtFixed } from '../utils/format';
// ============ 3D BLOCKCHAIN NODE NETWORK (Mouse-interactive) ============
const BlockchainNodes = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const nodesRef = useRef([]);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = canvas.parentElement.offsetWidth;
      canvas.height = canvas.parentElement.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const nodeCount = window.innerWidth < 768 ? 30 : 55;
    nodesRef.current = Array.from({ length: nodeCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      z: Math.random() * 200,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      vz: (Math.random() - 0.5) * 0.15,
      size: Math.random() * 3 + 1.5,
      pulse: Math.random() * Math.PI * 2,
    }));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const { x: mx, y: my } = mouseRef.current;
      const nodes = nodesRef.current;
      const time = Date.now() * 0.001;

      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        n.z += n.vz;
        n.pulse += 0.02;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        if (n.z < 0 || n.z > 200) n.vz *= -1;

        // Mouse repulsion
        const dx = n.x - mx;
        const dy = n.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 160 && dist > 0) {
          const force = (160 - dist) / 160 * 0.7;
          n.x += (dx / dist) * force;
          n.y += (dy / dist) * force;
        }
      });

      // Draw connections (purple)
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 140) {
            const alpha = (1 - d / 140) * 0.25;
            const depth = 1 - (nodes[i].z + nodes[j].z) / 400;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(168, 85, 247, ${alpha * depth})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      nodes.forEach((n) => {
        const depthScale = 1 - n.z / 300;
        const size = n.size * depthScale + Math.sin(n.pulse) * 0.5;
        const alpha = 0.4 + depthScale * 0.6;

        // Glow
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, size * 3);
        grad.addColorStop(0, `rgba(168, 85, 247, ${alpha * 0.25})`);
        grad.addColorStop(1, 'rgba(168, 85, 247, 0)');
        ctx.beginPath();
        ctx.arc(n.x, n.y, size * 3, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Core (purple)
        ctx.beginPath();
        ctx.arc(n.x, n.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(168, 85, 247, ${alpha})`;
        ctx.fill();

        // Gold accent on larger nodes
        if (n.size > 3) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, size * 0.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(232, 163, 23, ${alpha * 0.9})`;
          ctx.fill();
        }
      });

      // Floating gold data packets
      for (let i = 0; i < 6; i++) {
        const t = (time * 0.25 + i * 1.1) % nodes.length;
        const idx = Math.floor(t);
        const next = (idx + 1) % nodes.length;
        const frac = t - idx;
        const px = nodes[idx].x + (nodes[next].x - nodes[idx].x) * frac;
        const py = nodes[idx].y + (nodes[next].y - nodes[idx].y) * frac;
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(232, 163, 23, 0.85)';
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  const handleMouseMove = useCallback((e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const handleTouchMove = useCallback((e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const touch = e.touches[0];
    if (rect && touch) mouseRef.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: 'all' }}
    />
  );
};

// ============ LIVE PRICE TICKER ============
const LivePriceTicker = () => {
  const [prices, setPrices] = useState({ btc: 75770, eth: 2253, btcChange: -0.71, ethChange: -1.45 });
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum&vs_currencies=usd&include_24hr_change=true');
        const data = await res.json();
        setPrices({
          btc: data.bitcoin?.usd || 75770,
          eth: data.ethereum?.usd || 2253,
          btcChange: data.bitcoin?.usd_24h_change || 0,
          ethChange: data.ethereum?.usd_24h_change || 0,
        });
        setPulse(true);
        setTimeout(() => setPulse(false), 700);
      } catch { /* keep defaults */ }
    };
    fetchPrices();
    const iv = setInterval(fetchPrices, 25000);
    return () => clearInterval(iv);
  }, []);

  const fmt = (p) => p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtC = (c) => (c >= 0 ? '+' : '') + fmtFixed(c, 2) + '%';

  return (
    <div className={`inline-flex items-center gap-4 sm:gap-6 px-4 sm:px-6 py-2.5 rounded-full border transition-all duration-500 ${pulse ? 'border-purple-400/60 shadow-[0_0_25px_rgba(168,85,247,0.2)]' : 'border-white/10'}`}
      style={{ background: 'rgba(15, 10, 30, 0.6)', backdropFilter: 'blur(12px)' }}
      data-testid="live-ticker"
    >
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full bg-green-400 ${pulse ? 'animate-ping' : ''}`} />
        <span className="text-gray-400 text-xs font-medium tracking-wide">LIVE</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-white font-bold text-xs sm:text-sm">BTC</span>
        <span className="text-purple-300 font-mono text-xs sm:text-sm">${fmt(prices.btc)}</span>
        <span className={`text-xs font-semibold ${prices.btcChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtC(prices.btcChange)}</span>
      </div>
      <div className="w-px h-4 bg-white/10" />
      <div className="flex items-center gap-2">
        <span className="text-white font-bold text-xs sm:text-sm">ETH</span>
        <span className="text-purple-300 font-mono text-xs sm:text-sm">${fmt(prices.eth)}</span>
        <span className={`text-xs font-semibold ${prices.ethChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtC(prices.ethChange)}</span>
      </div>
    </div>
  );
};

// ============ SCROLL REVEAL ============
const ScrollReveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// ============ FEATURE CARD (Glassmorphism) ============
const FeatureCard = ({ icon, title, description, delay }) => (
  <ScrollReveal delay={delay}>
    <div className="group relative p-6 rounded-2xl border border-white/[0.08] overflow-hidden transition-all duration-300 hover:border-purple-500/40 hover:shadow-[0_0_35px_rgba(168,85,247,0.08)]"
      style={{ background: 'rgba(15, 10, 30, 0.5)', backdropFilter: 'blur(16px)' }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-purple-600/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative z-10">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 border border-purple-500/30 text-purple-400 group-hover:text-purple-300 group-hover:shadow-[0_0_15px_rgba(168,85,247,0.25)] transition-all duration-300"
          style={{ background: 'rgba(168, 85, 247, 0.08)' }}
        >
          {icon}
        </div>
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </ScrollReveal>
);

// ============ STAT CARD ============
const StatCard = ({ value, label, delay }) => (
  <ScrollReveal delay={delay}>
    <div className="text-center p-5 rounded-xl border border-white/[0.06]" style={{ background: 'rgba(15, 10, 30, 0.4)', backdropFilter: 'blur(8px)' }}>
      <div className="text-2xl sm:text-3xl font-bold" style={{ background: 'linear-gradient(135deg, #a855f7, #E8A317)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        {value}
      </div>
      <div className="text-gray-400 text-sm mt-1">{label}</div>
    </div>
  </ScrollReveal>
);

// ============ MAIN LANDING PAGE ============
const LandingPage = () => {
  const features = [
    { icon: <Shield className="h-6 w-6" />, title: 'Secure Vault Storage', description: 'Military-grade encryption with multi-sig cold storage protects your assets around the clock.' },
    { icon: <Zap className="h-6 w-6" />, title: 'AI Arbitrage Engine', description: 'Proprietary AI scans 200+ exchanges in real-time, capturing cross-market price discrepancies.' },
    { icon: <TrendingUp className="h-6 w-6" />, title: 'Binary Trading', description: 'Trade UP or DOWN on major crypto pairs with instant execution and up to 14% returns per trade.' },
    { icon: <Lock className="h-6 w-6" />, title: 'Smart Risk Management', description: 'Automated stop-loss, position sizing, and portfolio rebalancing powered by machine learning.' },
    { icon: <Globe className="h-6 w-6" />, title: 'Global Liquidity', description: 'Access deep liquidity pools across BTC, ETH, SOL, ADA, XRP and 50+ digital assets.' },
    { icon: <BarChart3 className="h-6 w-6" />, title: 'Real-Time Analytics', description: 'Professional-grade TradingView charts, volume heatmaps, and market sentiment indicators.' },
  ];

  return (
    <div className="min-h-screen text-white overflow-hidden" style={{ background: '#080510' }} data-testid="landing-page">
      {/* Global Styles */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .shimmer-btn {
          background-size: 200% auto;
          background-image: linear-gradient(90deg, #a855f7, #E8A317, #a855f7, #E8A317, #a855f7);
          animation: shimmer 3.5s linear infinite;
        }
        .glass-nav {
          background: rgba(8, 5, 16, 0.75);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .purple-glow { text-shadow: 0 0 30px rgba(168, 85, 247, 0.4); }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        .float-anim { animation: float 4s ease-in-out infinite; }
      `}</style>

      {/* NAVBAR */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Logo size="small" />
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-300">
            <a href="#features" className="hover:text-purple-400 transition-colors">Features</a>
            <a href="#stats" className="hover:text-purple-400 transition-colors">Performance</a>
            <a href="#security" className="hover:text-purple-400 transition-colors">Security</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/signin" className="text-sm text-gray-300 hover:text-white transition-colors px-3 py-2" data-testid="nav-signin">Sign In</Link>
            <Link to="/signup" className="text-sm font-semibold px-4 py-2 rounded-lg border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 transition-all" data-testid="nav-get-started">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
        {/* 3D Blockchain Node Network */}
        <div className="absolute inset-0 z-0">
          <BlockchainNodes />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#080510]/30 to-[#080510]" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <ScrollReveal>
            <div className="mb-8 flex justify-center">
              <LivePriceTicker />
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.1] mb-6 tracking-tight">
              Trade Crypto with
              <br />
              <span className="purple-glow" style={{ background: 'linear-gradient(135deg, #a855f7, #c084fc, #E8A317)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Phantom Intelligence
              </span>
            </h1>
          </ScrollReveal>

          <ScrollReveal delay={220}>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
              Next-generation AI arbitrage and binary trading platform. Secure, lightning-fast, and intelligent — built for traders who demand an edge.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={320}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link
                to="/signup"
                className="shimmer-btn text-black font-bold px-9 py-4 rounded-xl text-base shadow-[0_0_35px_rgba(168,85,247,0.3)] hover:shadow-[0_0_55px_rgba(168,85,247,0.5)] transition-shadow"
                data-testid="get-started-btn"
              >
                Get Started Free
                <ArrowRight className="inline-block ml-2 h-4 w-4" />
              </Link>
              <a href="#features" className="text-gray-400 hover:text-white text-sm flex items-center gap-2 transition-colors group">
                Explore Features <ChevronDown className="h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={420}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-sm text-gray-500">
              <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-purple-400" /> Bank-Level Security</div>
              <div className="flex items-center gap-2"><Zap className="h-4 w-4" style={{ color: '#E8A317' }} /> Sub-second Execution</div>
              <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-purple-400" /> 50+ Assets</div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* STATS */}
      <section id="stats" className="py-16 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard value="$2.4B+" label="Trading Volume" delay={0} />
          <StatCard value="180K+" label="Active Traders" delay={100} />
          <StatCard value="99.99%" label="Uptime" delay={200} />
          <StatCard value="<50ms" label="Execution Speed" delay={300} />
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Built for <span style={{ color: '#E8A317' }}>Serious Traders</span>
              </h2>
              <p className="text-gray-400 text-base max-w-xl mx-auto">
                Every feature engineered for speed, security, and maximum returns.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <FeatureCard key={i} {...f} delay={i * 80} />
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section id="security" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="relative p-8 sm:p-12 rounded-3xl border border-white/[0.08] overflow-hidden"
              style={{ background: 'rgba(15, 10, 30, 0.5)', backdropFilter: 'blur(20px)' }}
            >
              <div className="absolute top-0 right-0 w-72 h-72 bg-purple-600/[0.04] rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full blur-3xl" style={{ background: 'rgba(232, 163, 23, 0.03)' }} />

              <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">Your Assets. <span style={{ color: '#E8A317' }}>Fortress-Protected.</span></h2>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    Multi-signature cold wallets, real-time threat detection, and admin-verified withdrawals keep your funds untouchable.
                  </p>
                  <div className="space-y-3">
                    {['256-bit AES Encryption', 'Multi-sig Cold Storage', 'Admin-Verified Withdrawals', 'Real-Time Threat Monitoring'].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center border border-purple-500/40" style={{ background: 'rgba(168, 85, 247, 0.1)' }}>
                          <div className="w-2 h-2 rounded-full bg-purple-400" />
                        </div>
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="float-anim w-48 h-48 rounded-full border border-purple-500/20 flex items-center justify-center relative">
                    <div className="absolute inset-0 rounded-full border border-purple-500/10 animate-ping" style={{ animationDuration: '3s' }} />
                    <div className="w-32 h-32 rounded-full flex items-center justify-center" style={{ background: 'rgba(168, 85, 247, 0.08)', border: '1px solid rgba(168, 85, 247, 0.25)' }}>
                      <Shield className="h-14 w-14 text-purple-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <ScrollReveal>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Trade with an{' '}
              <span style={{ background: 'linear-gradient(135deg, #a855f7, #E8A317)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Unfair Advantage?
              </span>
            </h2>
            <p className="text-gray-400 mb-8 text-base max-w-lg mx-auto">
              Join 180,000+ traders using Phantom Trades AI-powered tools to generate consistent returns.
            </p>
            <Link
              to="/signup"
              className="shimmer-btn inline-flex items-center gap-2 text-black font-bold px-10 py-4 rounded-xl text-lg shadow-[0_0_35px_rgba(168,85,247,0.3)] hover:shadow-[0_0_55px_rgba(168,85,247,0.5)] transition-shadow"
              data-testid="cta-get-started"
            >
              Start Trading Now <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/[0.04] py-10 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="small" />
          <p className="text-gray-500 text-xs">&copy; {new Date().getFullYear()} Phantom Trades. All rights reserved.</p>
          <div className="flex gap-5 text-xs text-gray-500">
            <a href="#" className="hover:text-gray-300 transition-colors">Privacy</a>
            <a href="#" className="hover:text-gray-300 transition-colors">Terms</a>
            <a href="#" className="hover:text-gray-300 transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
